import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid, makeStyles } from "@material-ui/core";
import * as React from "react";
import { useForm, Controller } from "react-hook-form";
import { useRecoilValue } from "recoil";

import { CustomButton } from "@/components/Button";
import { CustomForm } from "@/components/property_edit/PropertyEditInputs";
import theme from "@/theme";
import { parseFormattedNumber } from "@/utilities/format";
import { buildingCostSchema } from "@/validations/management/common";
import { SelectedProperty } from "@/view_models/propertySelectors";

import { usePatchProperty } from "../../../hooks/usePatchProperty";
import { EditPropertyHistoryItem } from "../../EditPropertyHistoryItem";

import type { z } from "zod";

type BuildingCostFormData = z.infer<typeof buildingCostSchema>;

const useStyles = makeStyles(() => ({
  background: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    padding: 16,
  },
}));

export const BuildingBasicCostEditor: React.FC = () => {
  const classes = useStyles();
  const {
    control,
    formState: { errors },
    getValues,
    trigger,
  } = useForm<BuildingCostFormData>({
    resolver: zodResolver(buildingCostSchema),
  });
  const [isEditing, setIsEditing] = React.useState(false);
  const { patchProperty } = usePatchProperty();

  // 選択済み物件取得
  const selected_property = useRecoilValue(SelectedProperty);

  // 現在の値を取得
  const getCurrentValues = () => {
    return {
      management_company_commission: selected_property?.property?.building_detail?.management_company_commission,
      shared_utility_expenses: selected_property?.property?.building_detail?.shared_utility_expenses,
      misc_building_cost: selected_property?.property?.building_detail?.misc_building_cost,
    };
  };

  const getDisplayContents = () => {
    const currentValues = getCurrentValues();
    return [
      {
        label: "管理会社委託費（建物＋賃貸管理含む）",
        value: `${(currentValues.management_company_commission || 0).toLocaleString()}円`,
      },
      {
        label: "共用部水道光熱費",
        value: `${(currentValues.shared_utility_expenses || 0).toLocaleString()}円`,
      },
      {
        label: "その他経費",
        value: `${(currentValues.misc_building_cost || 0).toLocaleString()}円`,
      },
    ];
  };

  const onSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) {
      return;
    }

    const values = getValues();
    const sendData = {
      ...selected_property.property,
      management_company_commission: values.management_company_commission
        ? parseFormattedNumber(values.management_company_commission)
        : null,
      shared_utility_expenses: values.shared_utility_expenses
        ? parseFormattedNumber(values.shared_utility_expenses)
        : null,
      misc_building_cost: values.misc_building_cost ? parseFormattedNumber(values.misc_building_cost) : null,
    };

    await patchProperty(sendData);
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const currentValues = getCurrentValues();

  return (
    <>
      {isEditing ? (
        <Box className={classes.background} data-edit-form-panel>
          <Box mb={2}>
            <Controller
              name="management_company_commission"
              control={control}
              defaultValue={currentValues.management_company_commission || ""}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="管理会社委託費（建物＋賃貸管理含む）"
                  error={!!errors.management_company_commission}
                  errorText={errors.management_company_commission?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
            <Controller
              name="shared_utility_expenses"
              control={control}
              defaultValue={currentValues.shared_utility_expenses || ""}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="共用部水道光熱費"
                  error={!!errors.shared_utility_expenses}
                  errorText={errors.shared_utility_expenses?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
            <Controller
              name="misc_building_cost"
              control={control}
              defaultValue={currentValues.misc_building_cost || ""}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="その他経費"
                  error={!!errors.misc_building_cost}
                  errorText={errors.misc_building_cost?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
              info
            />
          </Box>
          <Grid container spacing={2} data-edit-form-actions>
            <Grid item xs={6}>
              <CustomButton customVariant="textPrimary" onClick={handleCancel} size="small" fullWidth>
                キャンセル
              </CustomButton>
            </Grid>
            <Grid item xs={6}>
              <CustomButton buttonSize="L" customVariant="filled" onClick={onSubmit} size="small" fullWidth>
                保存
              </CustomButton>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <EditPropertyHistoryItem preserveLabels contents={getDisplayContents()} onEdit={handleEdit} />
      )}
    </>
  );
};
